import { BackgroundProcess } from 'src/data-capture/hooks/file-uploads/types';
import { Dispatch, SetStateAction } from 'react';
import { Family } from 'src/clients/hooks/useFamily';
import { TemplateWithoutSections } from 'src/data-capture/hooks/templates/useTemplates';
import { ClientListItem } from 'src/admin/users/types';

export type MeetingNoteTemplateType = 'template' | 'agenda';
export type MeetingNoteSourceType = 'audio' | 'document';

const MEETING_NOTE_STEPS = ['client', 'source', 'structure', 'template', 'processing'] as const;
export type MeetingNoteStepId = (typeof MEETING_NOTE_STEPS)[number];

export type MeetingNoteFlowData = {
  selectedRecordingId?: string;
  selectedSourceType?: MeetingNoteSourceType;
  selectedSourceName?: string;
  templateType?: MeetingNoteTemplateType;
  template?: TemplateWithoutSections;
  selectedAgendaFileId?: string;
  selectedMeetingAgendaId?: string;
  selectedAgendaFileName?: string;
  agendaInstruction?: string;
  additionalInstructions?: string;
  summarise: boolean;
  isLoading?: boolean;
  fileOwnerClientId?: string;
} & ({ isFamily: true; family?: Family } | { isFamily: false; client?: ClientListItem });

export type MeetingNoteStepProps = {
  data: MeetingNoteFlowData;
  setData: Dispatch<SetStateAction<MeetingNoteFlowData>>;
  onNext: () => void;
  filter?: string;
  searchBar?: React.ReactNode;
};

export enum MeetingNoteStatus {
  READY = 'READY',
  MEETING_NOTE_PROCESSING = 'MEETING_NOTE_PROCESSING',
  TRANSCRIPTION_PROCESSING = 'TRANSCRIPTION_PROCESSING',
  FAILED = 'FAILED',
  NOT_CREATED = 'NOT_CREATED',
}

type MeetingNoteType = 'GENERAL';

/**
 * Mirrors the API's `MeetingNoteMetadataResponse`: what list endpoints return. There is no
 * `markdownContent` here; a note's body only comes back from the single-note endpoints, typed as
 * `MeetingNote`, so reading it off a list item is a type error rather than an `undefined`.
 */
export type MeetingNoteMetadata = {
  status: MeetingNoteStatus;
  id?: string;
  clientId?: string;
  templateId?: string;
  backgroundProcessId?: string;
  backgroundProcess?: BackgroundProcess;
  noteType?: MeetingNoteType;
  createdAt?: string;
  dataUploadFileId?: string;
  templatisedFileId?: number;
  fileName?: string;
  eventId?: string;
  documentHistoryId?: string;
  generatedFromAgenda?: boolean;
  agendaFileId?: string;
  meetingAgendaId?: string;
  isReviewed?: boolean;
};

/**
 * What has moved on since a note was generated, so the note may no longer describe the meeting
 * correctly. Attendee changes rename the people the note talks about; speaker changes move
 * statements between them.
 */
export type MeetingNoteStaleness = 'ATTENDEES' | 'SPEAKERS' | 'ATTENDEES_AND_SPEAKERS';

/** Mirrors the API's `GetMeetingNoteResponse`: a single note with its body. */
export type MeetingNote = MeetingNoteMetadata & {
  markdownContent?: string;
  isOutOfDate?: boolean;
  outOfDateReason?: MeetingNoteStaleness;
};
